import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { downloadFile } from '@/helpers/download';
import prepareTranslate from '@/helpers/dictionary';
import { getItemValue, joinFileName, splitFileName } from '@/helpers/string';
import { Button, Stack, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import s3Mutations from '@/api/mutitations/s3';
import paymentProofMutations from '@/api/mutitations/paymentProof';
import { IconDownload, IconEdit } from '@tabler/icons-react';
import CustomDropZone from '../CustomDropZone';
import Table from '../Table';
import { extractFilterOptions, filterAndSortData } from '../Table/Table.functions';
import DetailsModal from '../DetailsModal';
import EditDocuments from '../EditDocuments';
import Modal from '../Modal/Index';

const FILE_KEYS = [
  'agentFiles',
  'personalDetails.policyEndDateFiles',
  'personalDetails.taxDeductionsDateFiles',
  'personalDetails.agentLicenseDateFile',
  'emails.files',
  'expenses.files',
  'agreements.files',
  'additionalFees.files',
];

const ViewAgentFiles = (props) => {
  const {
    id: agentId,
    agent,
    handleAgentUpdateClick,
    setRefreshkey,
    refreshkey,
  } = props;

  const t = prepareTranslate();

  const { useDelete, useDownload } = s3Mutations;
  const { mutateAsync: downloadMutate } = useDownload();
  const { mutateAsync: deleteMutate } = useDelete();
  const { mutateAsync: getAllPaymentProofMutation } = paymentProofMutations.useGetAll();

  const [opened, { open, close }] = useDisclosure();
  const [responseOpened, { open: responseOpen, close: responseClose }] = useDisclosure();

  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [responseModalLabel, setResponseModalLabel] = useState('');
  const [filterOptions, setFilterOptions] = useState({});
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState({});
  const [newFiles, setNewFiles] = useState([]);

  const downloadFileByPath = async (_, row) => {
    const { name, path } = row;
    try {
      const response = await downloadMutate({ path });
      const { file: fileData, contentType } = response;
      downloadFile(fileData, name, contentType);
    } catch (error) {
      setResponseModalLabel(t('components.viewAgentFiles.downloadFaild.message'));
      responseOpen();
    }
  };

  useEffect(() => {
    if (!agent) return;

    const agentFiles = FILE_KEYS.flatMap((sourceKey) => {
      const filesFromKey = getItemValue(agent, sourceKey) || [];

      return filesFromKey.map((file, index) => {
        const fullName = file?.name || '';
        const lastDotIndex = fullName.lastIndexOf('.');

        const fileName = lastDotIndex !== -1 ? fullName.substring(0, lastDotIndex) : fullName;
        const fileType = lastDotIndex !== -1 ? fullName.substring(lastDotIndex + 1).toLowerCase() : '';

        return {
          ...file,
          fileName,
          fileType,
          sourceKey,
          canEdit: true,
          id: index,
        };
      });
    });

    const fetchPaymentProofFiles = async () => {
      try {
        const result = await getAllPaymentProofMutation({
          start: 1,
          end: 1000000,
          query: { associatedAgentsString: agentId },
        });

        const proofFiles = (result.data || [])
          .flatMap((proof) => (proof.files || []).map((file) => {
            const fullName = file?.name || '';
            const lastDotIndex = fullName.lastIndexOf('.');
            return {
              ...file,
              fileName: lastDotIndex !== -1 ? fullName.substring(0, lastDotIndex) : fullName,
              fileType: lastDotIndex !== -1 ? fullName.substring(lastDotIndex + 1).toLowerCase() : '',
              sourceKey: 'paymentProofs',
              paymentProofNumber: proof.paymentProofNumber,
              date: file.date || proof.createdAt,
            };
          }));

        setFiles([...agentFiles, ...proofFiles]);
      } catch {
        setFiles(agentFiles);
      }
    };

    fetchPaymentProofFiles();
  }, [agent, refreshkey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccept = (acceptedFiles) => {
    const formattedFiles = acceptedFiles.map((file) => ({ file, name: file?.name }));
    setNewFiles((prev) => [...prev, ...formattedFiles]);
  };

  const handleClickOnEdit = (_, row) => {
    const { baseName, extension } = splitFileName(row.name);
    setSelectedFile({
      ...row,
      baseName,
      extension,
    });

    open();
  };

  const getData = async (start, end, query, sort) => {
    if (!files || !Array.isArray(files)) {
      return { data: [], count: 0 };
    }

    const newFilterOptions = extractFilterOptions(files, mockColumns);
    setFilterOptions(newFilterOptions);
    const filteredAndSorted = filterAndSortData(files, query, sort);

    return { data: filteredAndSorted, count: filteredAndSorted.length };
  };

  const globalColumnsProps = {
    isVisible: true,
    isFilterActive: true,
    sortable: true,
  };

  const mockColumns = [
    {
      title: t('components.viewAgentFiles.fileName'),
      accessor: 'fileName',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentFiles.fileType'),
      accessor: 'fileType',
      ...globalColumnsProps,
    },
    {
      title: t('pages.paymentConfirmation.table.titles.paymentProofNumber'),
      accessor: 'paymentProofNumber',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentFiles.date'),
      accessor: 'date',
      filterType: 'datePicker',
      transforms: [
        {
          func: 'date',
          params: {
            withTime: false,
          },
        },
      ],
      ...globalColumnsProps,
    },
    {

      title: t('components.viewAgentFiles.actions'),
      actions: [
        {
          title: t('components.viewAgentFiles.download'),
          action: (id, row, title) => downloadFileByPath(id, row, title),
          icon: <IconDownload />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: t('components.viewAgentFiles.editFile'),
          action: (id, row, title) => handleClickOnEdit(id, row, title),
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
          showAction: 'canEdit',
        },
      ],
    },
  ];

  const initialColumnFilters = mockColumns.filter((column) => column.accessor !== 'actions').reduce((acc, column) => {
    acc[column.accessor] = '';
    return acc;
  }, {});

  const [columnFilters, setColumnFilters] = useState(initialColumnFilters);

  const handleUpdateAgent = async (key, value) => {
    await handleAgentUpdateClick({
      [key]: value,
    });
  };

  const updateAgent = async () => {
    setUpdateLoading(true);
    try {
      if (!selectedFile?.sourceKey) return;
      const { sourceKey } = selectedFile;

      const updatedArray = updateFileArray(agent, sourceKey, selectedFile);
      await handleUpdateAgent(sourceKey, updatedArray);

      close();
      setResponseModalLabel(t('components.viewAgentFiles.notification.success.title'));
      responseOpen();
    } catch (error) {
      setResponseModalLabel(t('components.viewAgentFiles.notification.success.title'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (!selectedFile?.sourceKey) return;

      const { path, sourceKey } = selectedFile;
      await deleteMutate({ path });

      const updatedArray = deleteFileFromArray(agent, sourceKey, selectedFile._id);
      await handleUpdateAgent(sourceKey, updatedArray);

      setResponseModalLabel(t('components.viewAgentFiles.notification.successDelete.title'));
      responseOpen();
      close();
      setRefreshkey((prev) => prev + 1);
    } catch (error) {
      setResponseModalLabel(t('components.viewAgentFiles.notification.failure.message'));
      responseOpen();
    } finally {
      setDeleteLoading(false);
    }
  };

  const updateFileArray = (data, sourceKey, updatedFile) => {
    const currentArray = getItemValue(data, sourceKey) || [];
    return currentArray.map((file) => {
      if (file._id === updatedFile._id) {
        return { ...file, name: updatedFile?.name };
      }
      return file;
    });
  };

  const deleteFileFromArray = (data, sourceKey, fileId) => {
    const currentArray = getItemValue(data, sourceKey) || [];
    return currentArray.filter((file) => file.id !== fileId);
  };

  const editFileActions = [
    <Button key='save' onClick={updateAgent} loading={updateLoading}>
      {t('global.save')}
    </Button>,
    <Button key='cancel' onClick={close} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  const editFileLeftActions = [
    <Button
      key='deleteFile'
      variant='outline'
      onClick={handleDelete}
      loading={deleteLoading}
    >
      {t('components.viewAgentFiles.deleteFile')}
    </Button>,
  ];

  const responseModalActions = [
    <Button key='close' onClick={responseClose}>
      {t('global.close')}
    </Button>,
  ];

  const handleFileNameChange = (newName) => {
    setSelectedFile({
      ...selectedFile,
      baseName: newName,
      name: joinFileName(newName, selectedFile.extension || ''),
    });
  };

  return (
    <>
      <Stack h='8rem' mb='2rem'>
        <CustomDropZone handleAccept={handleAccept} />
      </Stack>

      {newFiles.length > 0 && (
        <EditDocuments
          files={newFiles}
          setFiles={setNewFiles}
          agentId={agentId}
          openModal={responseOpen}
          setModalLabel={setResponseModalLabel}
          setKey={setRefreshkey}
        />
      )}

      {agent && (
        <Table
          isExportActive={false}
          columns={mockColumns}
          getData={getData}
          key={files?.length}
          isColumnsActive={false}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          filterOptions={filterOptions}
        />
      )}

      <DetailsModal
        label={t('components.viewAgentFiles.editFile')}
        opened={opened}
        close={close}
        open={open}
        actions={editFileActions}
        leftActions={editFileLeftActions}
      >
        <TextInput
          required
          label={t('components.viewAgentFiles.fileName')}
          value={selectedFile.baseName || ''}
          onChange={(e) => { handleFileNameChange(e.target.value); }}
        />
      </DetailsModal>

      <Modal
        opened={responseOpened}
        close={responseClose}
        label={responseModalLabel}
        actions={responseModalActions}
      />
    </>
  );
};

ViewAgentFiles.propTypes = {
  id: PropTypes.string,
  agent: PropTypes.object,
  handleAgentUpdateClick: PropTypes.func,
  setRefreshkey: PropTypes.func,
  refreshkey: PropTypes.number,
};

export default ViewAgentFiles;
